import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { DetailPanel, type PanelSpan } from '@/components/shared';
import type { BackofficeMessageBusSubscriberSummary } from '../types';

type MessageBusBarSortField = 'topic' | 'service' | 'count';
type SortDirection = 'asc' | 'desc';

interface SortState {
  field: MessageBusBarSortField;
  direction: SortDirection;
}

const textCollator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

export interface MessageBusBarTileRow extends BackofficeMessageBusSubscriberSummary {
  topicHref?: string;
  subscriberHref?: string;
}

export interface MessageBusBarTileProps {
  title: string;
  rows: MessageBusBarTileRow[];
  emptyLabel: string;
  span?: PanelSpan;
  currentSpan?: PanelSpan;
  panelId?: string;
}

function LabelLink(props: { to: string | undefined; children: string }) {
  if (!props.to) {
    return <span className="truncate">{props.children}</span>;
  }

  return (
    <Link
      to={props.to}
      className="truncate rounded-sm font-medium text-neutral-900 hover:text-backoffice-600"
    >
      {props.children}
    </Link>
  );
}

function SortButton({
  label,
  field,
  sort,
  align = 'left',
  onSortChange,
}: {
  label: string;
  field: MessageBusBarSortField;
  sort: SortState;
  align?: 'left' | 'right';
  onSortChange: (field: MessageBusBarSortField) => void;
}) {
  const active = sort.field === field;
  const Icon = active ? (sort.direction === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;

  return (
    <button
      type="button"
      className={[
        'focus-ring group/sort inline-flex w-fit items-center gap-1 rounded-sm text-left font-semibold text-neutral-500 transition-colors hover:text-neutral-900',
        align === 'right' ? 'ml-auto' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      aria-label={`Sort by ${label}`}
      aria-pressed={active}
      data-sort-direction={active ? sort.direction : undefined}
      onClick={() => onSortChange(field)}
    >
      <span>{label}</span>
      <Icon
        className={[
          'h-3.5 w-3.5 shrink-0 transition-opacity',
          active
            ? ''
            : 'opacity-0 group-hover/sort:opacity-100 group-focus-visible/sort:opacity-100',
        ]
          .filter(Boolean)
          .join(' ')}
        data-testid={`sort-icon-${field}`}
        aria-hidden="true"
      />
    </button>
  );
}

function compareRows(
  left: MessageBusBarTileRow,
  right: MessageBusBarTileRow,
  field: MessageBusBarSortField
): number {
  if (field === 'count') {
    return left.currentMessageCount - right.currentMessageCount;
  }

  return textCollator.compare(
    field === 'topic' ? left.topicName : left.subscriberName,
    field === 'topic' ? right.topicName : right.subscriberName
  );
}

export function MessageBusBarTile({
  title,
  rows,
  emptyLabel,
  span = '4x3',
  currentSpan,
  panelId,
}: MessageBusBarTileProps) {
  const [sort, setSort] = useState<SortState>({ field: 'count', direction: 'desc' });
  const maxValue = Math.max(1, ...rows.map((row) => row.currentMessageCount));
  const sortedRows = useMemo(
    () =>
      [...rows].sort((left, right) => {
        const comparison = compareRows(left, right, sort.field);
        return sort.direction === 'asc' ? comparison : -comparison;
      }),
    [rows, sort]
  );

  function handleSortChange(field: MessageBusBarSortField) {
    setSort((current) => ({
      field,
      direction: current.field === field && current.direction === 'asc' ? 'desc' : 'asc',
    }));
  }

  return (
    <DetailPanel
      title={title}
      span={span}
      currentSpan={currentSpan}
      panelId={panelId}
      contentOverflow="auto"
    >
      {rows.length === 0 ? (
        <div className="py-10 text-center text-body text-neutral-500">{emptyLabel}</div>
      ) : (
        <div className="min-w-[760px]">
          <div className="grid grid-cols-[minmax(0,1.2fr)_minmax(0,1.5fr)_minmax(160px,1fr)_4rem] gap-4 border-b border-neutral-200 pb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-neutral-500">
            <SortButton label="Topic" field="topic" sort={sort} onSortChange={handleSortChange} />
            <SortButton
              label="Service"
              field="service"
              sort={sort}
              onSortChange={handleSortChange}
            />
            <span>Value</span>
            <SortButton
              label="Count"
              field="count"
              sort={sort}
              align="right"
              onSortChange={handleSortChange}
            />
          </div>
          <div className="mt-3 space-y-3">
            {sortedRows.map((row) => {
              const activeWidth = row.currentMessageCount
                ? (row.activeMessageCount / maxValue) * 100
                : 0;
              const deadLetterWidth = row.currentMessageCount
                ? (row.deadLetterMessageCount / maxValue) * 100
                : 0;

              return (
                <div
                  key={`${row.topicName}:${row.subscriberName}`}
                  className="grid grid-cols-[minmax(0,1.2fr)_minmax(0,1.5fr)_minmax(160px,1fr)_4rem] items-center gap-4 text-sm"
                  data-testid="messagebus-bar-row"
                  data-topic-name={row.topicName}
                >
                  <LabelLink to={row.topicHref}>{row.topicName}</LabelLink>
                  <LabelLink to={row.subscriberHref}>{row.subscriberName}</LabelLink>
                  <div className="flex h-6 overflow-hidden rounded-full bg-neutral-100">
                    <div
                      className="bg-backoffice-500"
                      style={{ width: `${activeWidth}%` }}
                      title={`Active: ${row.activeMessageCount}`}
                    />
                    <div
                      className="bg-red-400"
                      style={{ width: `${deadLetterWidth}%` }}
                      title={`Dead-letter: ${row.deadLetterMessageCount}`}
                    />
                  </div>
                  <span className="text-right font-semibold tabular-nums text-neutral-900">
                    {row.currentMessageCount}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </DetailPanel>
  );
}
